import type { ComponentProps, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type TooltipRootProps = ComponentProps<typeof Tooltip>;
type TooltipContentProps = ComponentProps<typeof TooltipContent>;

interface IconTooltipButtonProps extends Omit<ComponentProps<typeof Button>, 'asChild'> {
  tooltip: ReactNode;
  tooltipProps?: Omit<TooltipRootProps, 'children'>;
  tooltipContentProps?: Omit<TooltipContentProps, 'children'>;
  /**
   * The element type to render as. Defaults to 'button'.
   * Use 'span' when nesting inside another button element to avoid nested button HTML validation errors.
   */
  as?: 'button' | 'span';
}

function IconTooltipButton({
  tooltip,
  tooltipProps,
  tooltipContentProps,
  size = 'icon',
  variant = 'ghost',
  colorScheme = 'secondary',
  as = 'button',
  className,
  onClick,
  disabled,
  ...buttonProps
}: IconTooltipButtonProps) {
  const mergedTooltipProps: TooltipRootProps = {
    delayDuration: 0,
    ...tooltipProps,
  };

  const resolvedAriaLabel = typeof tooltip === 'string' ? tooltip : undefined;

  const handleKeyDown =
    as === 'span' && onClick
      ? (e: KeyboardEvent<HTMLSpanElement>) => {
          if (disabled || (e.key !== 'Enter' && e.key !== ' ')) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          onClick(e as unknown as MouseEvent<HTMLButtonElement>);
        }
      : undefined;

  return (
    <Tooltip {...mergedTooltipProps}>
      <TooltipTrigger asChild>
        <Button
          asChild={as === 'span'}
          size={size}
          variant={variant}
          colorScheme={colorScheme}
          aria-label={resolvedAriaLabel}
          className={className}
          disabled={disabled}
          onClick={onClick}
          {...buttonProps}
        >
          {as === 'span' ? (
            <span
              {...(onClick && {
                tabIndex: disabled ? -1 : 0,
                'aria-disabled': disabled || undefined,
                onKeyDown: handleKeyDown,
              })}
            >
              {buttonProps.children}
            </span>
          ) : (
            buttonProps.children
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export { IconTooltipButton };
